import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from './useFocusTrap';
import { ExerciseThumbnail } from './ExerciseThumbnail';
import { ExerciseFilterBar } from './ExerciseFilterBar';
import { exerciseRepository, type ExerciseFacets } from '../../data/repositories/exerciseRepository';
import { taxonomyRepository, type TaxonomyLabels } from '../../data/repositories/taxonomyRepository';
import { formatExerciseName } from '../format/exerciseName';
import type { ExerciseRecord } from '../../data/types';
import dialogStyles from './Dialog.module.css';
import styles from './ExercisePickerDialog.module.css';

/**
 * How many rows to render at a time. Matches `ExerciseList`'s `PAGE_SIZE`
 * (the Exercises tab) — same catalogue size (1,500), same reasoning: render
 * a page at a time and grow via IntersectionObserver rather than mounting
 * every row up front.
 */
const PAGE_SIZE = 50;

/** Toggles `slug` off if already selected, else selects it — single-select per facet, the same rule `useExerciseSearch` applies to the Exercises tab's own filters. */
function toggleFacet(current: string | null, slug: string): string | null {
  return current === slug ? null : slug;
}

/**
 * Pick an exercise from the completed Phase 4 catalogue+custom system.
 * Reuses `exerciseRepository.search()` directly — the same function
 * `useExerciseSearch`/`ExercisesScreen` already call — rather than a second
 * search implementation (Phase 5's approved plan, §20). Filtering (Favourites,
 * Body Part, Target Muscle, Equipment — 2026-09-15) reuses the shared
 * `ExerciseFilterBar` component the Exercises tab already uses, for the exact
 * same reason: one filter UI, not a second one forked for this dialog.
 *
 * Lives under `ui/components` (not `features/plans`) because it's shared
 * across features: the plan builder's add/replace-exercise flow and the
 * active workout's free-workout add-exercise flow (Master Phase 11) both use
 * this same dialog rather than each forking their own copy. `ExerciseFilterBar`
 * was moved here from `features/exercises` for the same reason — no file
 * under `ui/components` may import from `features/*`.
 *
 * Archive removed from the user-facing app (2026-09-15): this dialog used to
 * hide archived exercises behind an "Include archived" toggle. That toggle
 * is gone — `exerciseRepository.search()` is always called with
 * `includeArchived: true` now, so an exercise archived before this change
 * appears exactly like any other exercise, never hidden.
 */
export function ExercisePickerDialog({
  onSelect,
  onCancel,
  renderPreview,
  selectLabel = 'Add Exercise',
}: {
  onSelect: (exercise: ExerciseRecord) => void;
  onCancel: () => void;
  /**
   * Optional. When supplied, choosing a row no longer selects it immediately —
   * it opens an inspect-before-adding step showing this content, with a
   * `selectLabel` button that performs the actual selection (UX pass,
   * 2026-09-05).
   *
   * A render prop rather than a hard import so this dialog stays in the
   * `ui/components` layer: no file here imports from `features/`, and the
   * exercise-detail components live in `features/exercises`. The caller — a
   * feature — supplies them, exactly as `PlanExerciseRow` already composes
   * `ExerciseDemonstration` + `ExerciseInfoDetails` for its own info panel.
   *
   * Omitted (the active-workout add-exercise flow) keeps the original
   * select-straight-from-the-list behaviour unchanged.
   */
  renderPreview?: (exercise: ExerciseRecord) => React.ReactNode;
  /** Label for the confirm action in the preview step. */
  selectLabel?: string;
}): React.JSX.Element {
  const searchId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [text, setText] = useState('');
  const [favouritesOnly, setFavouritesOnly] = useState(false);
  const [bodyPart, setBodyPart] = useState<string | null>(null);
  const [targetMuscle, setTargetMuscle] = useState<string | null>(null);
  const [equipment, setEquipment] = useState<string | null>(null);
  const [facets, setFacets] = useState<ExerciseFacets | null>(null);
  const [labels, setLabels] = useState<TaxonomyLabels | null>(null);
  const [results, setResults] = useState<ExerciseRecord[]>([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<ExerciseRecord | null>(null);
  const sentinelRef = useRef<HTMLLIElement | null>(null);
  const pickerListRef = useRef<HTMLUListElement | null>(null);
  /**
   * The list's own scroll position, captured the instant a row is tapped
   * into the inspect-before-adding step. `.pickerList` unmounts while
   * `preview` is set (it's a sibling branch of the same `{preview === null
   * && (...)}` conditional, not merely hidden), so the browser has already
   * forgotten its `scrollTop` by the time "‹ Back to list" brings it back —
   * without this, every return from a preview reset back to the top of the
   * results, however far down you'd scrolled to find that exercise.
   */
  const savedScrollTopRef = useRef(0);
  /**
   * Guards against a double-tap adding the same exercise twice.
   *
   * A ref, not just the `disabled` attribute: `onSelect` is asynchronous at
   * every call site (the plan builder awaits a repository write), so two
   * clicks dispatched before React re-renders would both get through a
   * state-only guard. Checked and set synchronously in the same tick.
   */
  const selectingRef = useRef(false);

  useFocusTrap(panelRef);

  // Mount-only: focuses the panel so `useFocusTrap`'s Tab cycle has
  // somewhere to start. Deliberately NOT in the effect below — `onCancel` is
  // a fresh inline function on every render of a caller with its own ticking
  // state (e.g. `ActiveWorkoutScreen`'s elapsed-workout timer re-renders
  // every second), and re-running this on every one of those renders yanked
  // focus off the search input back onto the dialog roughly once a second,
  // making it look like the field couldn't be typed into at all — the value
  // was actually still updating (`text` state), just losing focus (and, on a
  // real phone, the on-screen keyboard) moments after each keystroke.
  useEffect(() => {
    panelRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally mount-only, see comment above
  }, []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  // Facets and taxonomy labels are independent of the query — the options
  // should not vanish as you narrow, otherwise you cannot widen again
  // without clearing everything first (same rule `useExerciseSearch` follows
  // for the Exercises tab). Loaded once, on mount.
  useEffect(() => {
    let cancelled = false;
    void exerciseRepository.facets().then((found) => {
      if (!cancelled) setFacets(found);
    });
    void taxonomyRepository.labels().then((found) => {
      if (!cancelled) setLabels(found);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // No `limit` — this must search the entire catalogue (1,500 rows), not
    // just the first page. How many of those results actually render is a
    // separate, incremental concern handled by `visibleCount` below.
    //
    // `includeArchived: true` unconditionally — see the archive-removal note
    // in this component's own doc comment above.
    void exerciseRepository
      .search({
        ...(text.length > 0 ? { text } : {}),
        ...(bodyPart !== null ? { bodyPart } : {}),
        ...(targetMuscle !== null ? { targetMuscle } : {}),
        ...(equipment !== null ? { equipment } : {}),
        ...(favouritesOnly ? { favouritesOnly: true } : {}),
        includeArchived: true,
        sort: 'name',
      })
      .then((rows) => {
        if (!cancelled) {
          setResults(rows);
          setVisibleCount(PAGE_SIZE);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [text, favouritesOnly, bodyPart, targetMuscle, equipment]);

  // Grow the rendered slice as the sentinel scrolls into view within the
  // picker's own scroll container (`.pickerList`), the same pattern
  // `ExerciseList` uses for the Exercises tab over the same catalogue size.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (sentinel === null) return;
    if (visibleCount >= results.length) return;

    if (typeof IntersectionObserver === 'undefined') {
      setVisibleCount(results.length);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisibleCount((current) => Math.min(current + PAGE_SIZE, results.length));
        }
      },
      // The sentinel is the last <li> in `.pickerList`, which scrolls
      // internally rather than the page — the observer's root must be that
      // container (the sentinel's own parent <ul>), not the viewport, or
      // intersection never fires while it's off-screen within an
      // otherwise-fully-visible dialog.
      { root: sentinel.parentElement, rootMargin: '200px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [visibleCount, results.length]);

  const hasActiveFilters =
    text.length > 0 || bodyPart !== null || targetMuscle !== null || equipment !== null || favouritesOnly;

  function clearAllFilters(): void {
    setText('');
    setFavouritesOnly(false);
    setBodyPart(null);
    setTargetMuscle(null);
    setEquipment(null);
  }

  /** The one path that actually selects, wherever it is triggered from. */
  function commitSelection(exercise: ExerciseRecord): void {
    if (selectingRef.current) return;
    selectingRef.current = true;
    onSelect(exercise);
  }

  function handleRowClick(exercise: ExerciseRecord): void {
    if (renderPreview === undefined) {
      commitSelection(exercise);
      return;
    }
    if (pickerListRef.current !== null) {
      savedScrollTopRef.current = pickerListRef.current.scrollTop;
    }
    setPreview(exercise);
  }

  // Runs after the list re-mounts (a layout effect, not a passive one, so it
  // applies before the browser paints the restored frame — a passive effect
  // would let one frame render scrolled-to-top first). `results`/
  // `visibleCount` are untouched by opening or closing a preview, so the
  // list's content — and therefore its scrollable height — is exactly what
  // it was when `savedScrollTopRef` was captured.
  useLayoutEffect(() => {
    if (preview !== null) return;
    if (pickerListRef.current === null) return;
    pickerListRef.current.scrollTop = savedScrollTopRef.current;
  }, [preview]);

  return createPortal(
    <div className={styles.dialogOverlay} onClick={onCancel}>
      <div
        ref={panelRef}
        className={styles.dialogPanel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="exercise-picker-title"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        {/* ── Inspect-before-adding step ──────────────────────────────────
            Same exercise-detail content the Exercises tab shows, supplied by
            the caller, plus the one extra action this context adds. */}
        {preview !== null && (
          <>
            <div className={styles.previewHeader}>
              <button
                type="button"
                className={styles.actionButton}
                onClick={() => setPreview(null)}
              >
                ‹ Back to list
              </button>
            </div>

            <h2 id="exercise-picker-title" className={dialogStyles.dialogTitle}>
              {formatExerciseName(preview.name)}
            </h2>

            <div className={styles.previewBody}>{renderPreview?.(preview)}</div>

            <div className={styles.actions}>
              <button
                type="button"
                className={`${styles.actionButton} ${styles.actionOn}`}
                onClick={() => commitSelection(preview)}
              >
                {selectLabel}
              </button>
              <button type="button" className={styles.actionButton} onClick={onCancel}>
                Cancel
              </button>
            </div>
          </>
        )}

        {preview === null && (
          <>
        <h2 id="exercise-picker-title" className={dialogStyles.dialogTitle}>
          Add an exercise
        </h2>

        <div className={styles.field}>
          <label className={styles.formLabel} htmlFor={searchId}>
            Search exercises
          </label>
          <input
            id={searchId}
            type="search"
            className={styles.formInput}
            value={text}
            onChange={(event) => setText(event.target.value)}
            autoFocus
          />
        </div>

        <ExerciseFilterBar
          facets={facets}
          labels={labels}
          bodyPart={bodyPart}
          targetMuscle={targetMuscle}
          equipment={equipment}
          favouritesOnly={favouritesOnly}
          onToggleBodyPart={(slug) => setBodyPart((current) => toggleFacet(current, slug))}
          onToggleTargetMuscle={(slug) => setTargetMuscle((current) => toggleFacet(current, slug))}
          onToggleEquipment={(slug) => setEquipment((current) => toggleFacet(current, slug))}
          onToggleFavourites={() => setFavouritesOnly((current) => !current)}
          onClearAll={clearAllFilters}
          hasActiveFilters={hasActiveFilters}
          collapsibleFacets
        />

        {loading && (
          <p className={styles.stateText} role="status">
            Searching…
          </p>
        )}

        {!loading && results.length === 0 && (
          <p className={styles.stateText} role="status">
            {favouritesOnly
              ? 'You have no favourite exercises yet. Star an exercise from the Exercises tab or its preview here to add it.'
              : 'No exercises match.'}
          </p>
        )}

        {!loading && results.length > 0 && (
          <ul className={styles.pickerList} ref={pickerListRef}>
            {results.slice(0, visibleCount).map((exercise) => (
              <li key={exercise.id}>
                {/* Same row anatomy as the Exercises tab's own list rows
                    (`ExerciseListItem`): name on the left, badges, and a lazy
                    thumbnail on the right via the shared `ExerciseThumbnail`
                    — user media first, catalogue GIF only as a fallback, and
                    only for rows actually scrolled into view. */}
                <button
                  type="button"
                  className={styles.pickerItem}
                  onClick={() => handleRowClick(exercise)}
                >
                  <span className={styles.pickerItemName}>
                    {exercise.isFavourite && <span aria-hidden="true">★ </span>}
                    {formatExerciseName(exercise.name)}
                  </span>
                  <span className={styles.pickerItemThumb}>
                    <ExerciseThumbnail exercise={exercise} size={96} />
                  </span>
                </button>
              </li>
            ))}

            {visibleCount < results.length && (
              <li ref={sentinelRef} className={styles.sentinel}>
                <p className={styles.sentinelText}>
                  Showing {Math.min(visibleCount, results.length).toLocaleString()} of{' '}
                  {results.length.toLocaleString()}…
                </p>
                {/* Keyboard and reduced-motion users may never trigger the
                    observer; a real control guarantees the rest is reachable. */}
                <button
                  type="button"
                  className={styles.moreButton}
                  onClick={() =>
                    setVisibleCount((current) => Math.min(current + PAGE_SIZE, results.length))
                  }
                >
                  Show more
                </button>
              </li>
            )}
          </ul>
        )}

        <div className={styles.actions}>
          <button type="button" className={styles.actionButton} onClick={onCancel}>
            Cancel
          </button>
        </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
